import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActivityCountQuery, useMembersQuery, useWorkspaceActivityQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { routes } from '@platform/core/lib/routes';
import { useSince } from '@platform/features/users/inbox/useSince';
import { useDebounced } from '@platform/features/search/useDebounced';
import { useOpenInWorkspace } from '@platform/navigation';
import { useTypedRecordLabel } from '../../shell/strings/useTypedRecordLabel';
import { useWorkspaceCatalog } from '../../shell/useWorkspaceCatalog';
import { SysNote } from '../system/SysNote';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { auditModules } from './auditModules';
import { auditText } from './auditText';

const COLUMNS = '28px minmax(120px, 0.8fr) minmax(220px, 2fr) minmax(180px, 1.4fr) 100px';
const SHOWN = 150;
/** Сторінок стрічки по 100 подій — досить для 150 рядків таблиці; фільтрує сервер, кількість — його лічильник з тими самими фільтрами. */
const PAGES = 2;

/**
 * «Журнал аудиту» (прототип v4 G:L8218–8285, pgAdmAudit): «Кожна зміна в цьому робочому просторі — хто, що і коли.»; пошук, особа (усі
 * учасники простору в порядку членства), модуль (типи записів простору в порядку прототипу — auditModules); фільтрує сервер усі події
 * (автор, тип запису, пошук у коді, назві, підписі поля, значеннях і коментарях), «Показано 150 з N» / «N змін» — його лічильник з тими
 * самими фільтрами; таблиця «Хто · Зміна · Запис · Коли» («Тип номер · Назва», як у прототипі); рядок відкриває історію запису.
 */
export function AdminAuditPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const since = useSince();
  const open = useOpenInWorkspace();
  const { ws } = useWorkspaceParams();
  const [filter, setFilter] = useState({ q: '', who: '', module: '' });
  const q = useDebounced(filter.q.trim(), 300);
  const server = { ws, actor: filter.who || undefined, recordType: filter.module || undefined, q: q || undefined };
  const { data } = useWorkspaceActivityQuery({ ...server, limit: 100, pages: PAGES }, { skip: !ws });
  const { data: counted } = useActivityCountQuery({ ...server, days: 0 }, { skip: !ws });
  const { catalog } = useWorkspaceCatalog(ws);
  const { data: members } = useMembersQuery(ws, { skip: !ws });
  const items = useMemo(() => data?.items ?? [], [data]);
  const people = useMemo(() => (members ?? []).map((m): [string, string] => [m.user.id, m.user.name]), [members]);
  const modules = useMemo(() => auditModules(catalog, label), [catalog, label]);
  const shown = items.slice(0, SHOWN);
  const recordLabel = useTypedRecordLabel(ws, [...new Set(shown.map((i) => i.record.module ?? ''))]);
  const rows = shown.map((i) => ({
    key: String(i.id),
    onClick: () => { const r = i.record; if (r.id && r.module && r.recordType) open(r.workspaceId, routes.record(r.module, r.recordType, r.id, 'history')); },
    cells: [
      <span key="a" className="sp-avatar">{i.actor?.initials ?? ''}</span>, <span key="w" className="sp-strong">{i.actor?.name ?? ''}</span>, auditText(i, t),
      <span key="r" className="sp-muted" title={i.record.display ?? undefined}>{recordLabel(i.record)}</span>, <span key="t" className="sp-muted ad-right">{since(i.at)}</span>,
    ],
  }));
  const total = counted?.days === 0 ? counted.count : items.length;
  const count = total > SHOWN ? t('sysAdmin.audit.showing', { shown: SHOWN, total }) : t('sysAdmin.audit.changes', { count: total });
  const set = (patch: Partial<typeof filter>) => setFilter((f) => ({ ...f, ...patch }));
  return (
    <SystemPage title={t('sysPages.sections.audit')} sub={t('sysAdmin.audit.sub')}>
      {data?.available === false && <SysNote>{t('sysPages.adminHome.noFeed')}</SysNote>}
      <div className="sp-toolbar">
        <input className="sp-input ad-search" value={filter.q} placeholder={t('sysAdmin.audit.search')} aria-label={t('sysAdmin.audit.searchLabel')} onChange={(e) => set({ q: e.target.value })} />
        <select className="sp-select ad-filter" value={filter.who} aria-label={t('sysAdmin.audit.person')} onChange={(e) => set({ who: e.target.value })}>
          <option value="">{t('sysAdmin.audit.everyone')}</option>
          {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select className="sp-select ad-filter" value={filter.module} aria-label={t('sysAdmin.audit.module')} onChange={(e) => set({ module: e.target.value })}>
          <option value="">{t('sysAdmin.audit.allModules')}</option>
          {modules.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        {data?.available && <span className="sp-count">{count}</span>}
      </div>
      <SysTable columns={COLUMNS} minWidth={760} rows={rows} empty={data?.available ? t('sysAdmin.audit.none') : undefined} label={t('sysPages.sections.audit')}
        head={['', t('sysAdmin.audit.who'), t('sysAdmin.audit.change'), t('sysAdmin.audit.record'), <span key="w" className="ad-right">{t('sysAdmin.audit.when')}</span>]} />
    </SystemPage>
  );
}
